import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";
import FaqCard from "@/components/ui/faq-card";
import {
  FloatingBanner,
  FloatingBannerStack,
} from "@/components/ui/floating-banner";
import { Separator } from "@/components/ui/separator";
import {
  ContractStatus,
  statusIcon,
  statusVariant,
} from "@/utils/status-transport";
import { ArrowLeft } from "lucide-react";

import ContractDetailsSkeleton from "@/components/pages/client/contractDetails/contract-details-skeleton";
import { ContractDocumentsCard } from "@/components/pages/client/contractDetails/contract-documents-card";
import { DeliveryDateCard } from "@/components/pages/client/contractDetails/delivery-date-card";
import { FreightDetailsCard } from "@/components/pages/client/contractDetails/freight-details-card";
import { InspectionCard } from "@/components/pages/client/contractDetails/inspection-card";
import { InvoicesCard } from "@/components/pages/client/contractDetails/invoices-card";
import TransportTimelineV2Wrapper from "@/components/pages/client/contractDetails/transport-timeline-wrapper";
import { useAuth } from "@/contexts/auth-context";
import { getCotacaoDetalhes } from "@/services/clientAreaService";
import { CotacaoEcommerce } from "@/types/Cotacao";
import { Download, ExternalLink } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const CONTRACT_BASE_URL = `${import.meta.env.VITE_API_URL}/anexos/contratos`;

function getContractUrl(
  ctc_codigo: string | null | undefined,
  cli_codigo: string | null | undefined,
): string | null {
  if (!ctc_codigo || !cli_codigo) return null;
  return `${CONTRACT_BASE_URL}/contrato_${ctc_codigo}_${cli_codigo}.pdf`;
}

export default function ContractDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cotacao, setCotacao] = useState<CotacaoEcommerce | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showUnpaidBanner, setShowUnpaidBanner] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const invoicesSectionRef = useRef<HTMLDivElement>(null);

  const status = cotacao ? cotacao.cls_situacao || "--" : "--";

  const fetchCotacaoById = async (cve_codigo: string) => {
    try {
      const data = await getCotacaoDetalhes(cve_codigo);
      const cotacao = data[0];
      setCotacao(cotacao);
    } catch (error) {
      console.error("Erro ao sincronizar cotação:", error);
    }
  };

  useEffect(() => {
    if (!id) return;
    setShowUnpaidBanner(false);
    setBannerDismissed(false);
    const fetchCotacao = async () => {
      try {
        setIsLoading(true);
        const response = await getCotacaoDetalhes(id);
        if (response.length > 0) {
          setCotacao(response[0]);
        }
      } catch (error) {
        console.error("Erro ao buscar cotação:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCotacao();
  }, [id]);

  const handleHasUnpaidInvoices = useCallback((hasUnpaid: boolean) => {
    if (hasUnpaid) setShowUnpaidBanner(true);
  }, []);

  const handleScrollToInvoices = () => {
    const section = invoicesSectionRef.current;
    if (section) {
      const verticalOffset = Math.max(
        88,
        (window.innerHeight - section.offsetHeight) / 2,
      );
      const top =
        section.getBoundingClientRect().top + window.scrollY - verticalOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setBannerDismissed(true);
  };

  useEffect(() => {
    if (!showUnpaidBanner || bannerDismissed) return;

    const handleScroll = () => {
      const section = invoicesSectionRef.current;
      if (!section) return;
      const sectionBottom = section.offsetTop + section.offsetHeight;
      if (window.scrollY > sectionBottom + 300) {
        setBannerDismissed(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showUnpaidBanner, bannerDismissed]);

  if (isLoading) {
    return (
      <div className="py-6 md:py-10 max-w-7xl w-full mx-auto">
        <h1 className="mb-10 text-2xl sm:text-[32px] font-semibold text-n-text-green">
          Detalhes do Contrato
        </h1>
        <ContractDetailsSkeleton />
      </div>
    );
  }

  if (hasError || !cotacao) {
    return (
      <div className="px-4 sm:px-6 py-6 sm:py-10 w-full max-w-7xl items-center mx-auto">
        <h1 className="mb-10 text-2xl sm:text-[32px] font-semibold text-n-text-green">
          Detalhes do Contrato
        </h1>
        <p className="text-sm text-red-500">
          Ocorreu um erro ao carregar os detalhes do contrato.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="py-2 sm:py-10 w-full max-w-7xl items-center mx-auto">
        <div className="md:mb-10 mb-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-full hover:bg-n-green-1"
          >
            <ArrowLeft className="h-5 w-5 text-n-text-green" />
          </Button>
          <h1 className="text-2xl sm:text-[32px] font-semibold text-n-text-green">
            Detalhes do Contrato
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row lg:gap-8">
          <div className="w-full ">
            <div className="flex flex-col gap-4 rounded-2xl bg-white md:py-5 md:px-8 py-4 px-5 shadow-sm">
              <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ">
                <h2 className="text-lg font-semibold text-n-text-green">
                  Contrato nº&nbsp;{cotacao.ctc_codigo}
                </h2>

                <div className="flex items-center gap-2">
                  <span className="text-n-text-neutral">Status:</span>
                  <Badge
                    className="text-sm font-bold text-n-text-green rounded-full gap-2 px-2"
                    variant={`${statusVariant[status as ContractStatus]}`}
                  >
                    {status}
                    {statusIcon[status as ContractStatus]}
                  </Badge>
                </div>
              </header>

              {(() => {
                const contractUrl = getContractUrl(
                  cotacao.ctc_codigo,
                  user?.cli_codigo,
                );
                return contractUrl ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => window.open(contractUrl, "_blank", "noopener,noreferrer")}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver contrato
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        const a = document.createElement("a");
                        a.href = contractUrl;
                        a.download = `contrato_${cotacao.ctc_codigo}.pdf`;
                        a.click();
                      }}
                    >
                      <Download className="w-4 h-4" />
                      Baixar contrato
                    </Button>
                  </div>
                ) : null;
              })()}

              <Separator />

              {cotacao && <FreightDetailsCard cotacao={cotacao} />}

              {cotacao && Number(cotacao.cls_codigo) < 3 && (
                <>
                  <Separator />
                  <DeliveryDateCard
                    cotacao={cotacao}
                    onDateUpdate={fetchCotacaoById}
                  />
                </>
              )}

              <Separator />

              <div className="overflow-x-auto md:overflow-visible md:mb-4 mb-2">
                <TransportTimelineV2Wrapper
                  cve_codigo={cotacao.cve_codigo}
                  cve_chassi={cotacao.cve_chassi}
                />
              </div>
              <FaqCard
                title="Permanece com dúvida?"
                subtitle="Acesse nossa central de ajuda"
                buttonLabel="Central de ajuda"
                onClick={() => navigate("/cliente/ajuda/perguntas-frequentes")}
              />
              <Separator />

              <ContractDocumentsCard
                cotacao={cotacao}
                onUpdate={fetchCotacaoById}
              />

              <Separator />
              <div ref={invoicesSectionRef}>
                <InvoicesCard
                  cotacao={cotacao}
                  onHasUnpaidInvoices={handleHasUnpaidInvoices}
                />
              </div>
              {cotacao && cotacao.vis_token && (
                <>
                  <Separator />
                  <InspectionCard
                    cve_codigo={cotacao.cve_codigo}
                    vis_token={cotacao.vis_token}
                    cls_codigo={cotacao.cls_codigo}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <FloatingBannerStack>
        {showUnpaidBanner && !bannerDismissed && (
          <FloatingBanner
            message="Você tem faturas pendentes de pagamento."
            buttonLabel="Ver faturas"
            onButtonClick={handleScrollToInvoices}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
      </FloatingBannerStack>
    </>
  );
}
